import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  IconButton, 
  CircularProgress,
  Box,
  Typography,
  Chip,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import SaveIcon from '@mui/icons-material/Save';
import TrampolineScorecard from './TrampolineScorecard';
import competitionCardsService, { CompetitionCard, Exercise } from '../services/competitionCardsService';
import { handleApiError } from '../utils/apiErrorHandler';

interface TrampolineScorecardDialogProps {
  open: boolean;
  competitionId: string;
  clubId: string;
  athleteId: string;
  athleteName: string;
  competitionGroup: string;
  onClose: (hasData: boolean) => void;
  readOnly?: boolean;
  cardIndex?: number;
  cardTitle?: string;
}

// Interface für die von TrampolineScorecard erhaltenen Daten
interface ScorecardFormData {
  exercises: Exercise[];
  selectedRoutines: string[];
  checkboxes: boolean[];
  additionalMarkings: boolean[];
  validationStatus: {
    isComplete: boolean;
    isFirstExerciseValid: boolean;
    isFullyValid: boolean;
    isFirstExerciseSelected: boolean;
  };
}

const TrampolineScorecardDialog: React.FC<TrampolineScorecardDialogProps> = ({
  open,
  competitionId,
  clubId,
  athleteId,
  athleteName,
  competitionGroup,
  onClose,
  readOnly = false,
  cardIndex = 0,
  cardTitle
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [card, setCard] = useState<CompetitionCard | null>(null);
  const [formData, setFormData] = useState<ScorecardFormData | null>(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Statusanzeige-Helper
  const getStatusColor = (validationStatus: ScorecardFormData['validationStatus']) => {
    if (!validationStatus) return 'default';
    if (!validationStatus.isComplete) return 'warning';
    if (!validationStatus.isFirstExerciseValid) return 'error';
    return 'success';
  };

  // Statusanzeige-Helper
  const getStatusIcon = (validationStatus: ScorecardFormData['validationStatus']) => {
    if (!validationStatus) return undefined;
    if (!validationStatus.isComplete) return <WarningIcon />;
    if (!validationStatus.isFirstExerciseValid) return <ErrorIcon />;
    return <CheckCircleIcon />;
  };

  const getStatusText = (validationStatus: ScorecardFormData['validationStatus']) => {
    if (!validationStatus) return 'Unbekannt';
    if (!validationStatus.isComplete) return 'Unvollständig';
    if (!validationStatus.isFirstExerciseValid) return 'Fehlerhaft';
    return 'Vollständig';
  };

  // Laden der Daten, wenn der Dialog geöffnet wird
  useEffect(() => {
    if (open && !initialLoadComplete) {
      loadCardData();
    }
  }, [open]);

  const loadCardData = async () => {
    if (!competitionId || !clubId || !athleteId) return;
    
    try {
      setLoading(true);
      const cardData = await competitionCardsService.getCompetitionCard(
        competitionId,
        clubId,
        athleteId,
        cardIndex
      );
      
      setCard(cardData);
      
      // Wenn Daten gefunden wurden, diese für die Scorecard-Komponente vorbereiten
      if (cardData) {
        setFormData({
          exercises: cardData.exercises,
          selectedRoutines: cardData.selectedRoutines || [],
          checkboxes: cardData.checkboxes || [],
          additionalMarkings: cardData.additionalMarkings || [],
          validationStatus: {
            ...cardData.validationStatus,
            isFirstExerciseSelected: cardData.validationStatus.isFirstExerciseSelected ?? (cardData.selectedRoutines?.[0] !== "")
          }
        });
      } else {
        // Für neue Wettkampfkarten: Initialisiere mit korrekten Standardwerten
        setFormData({
          exercises: [
            { name: '', moves: Array(10).fill(''), totalDifficulty: 0 },
            { name: '', moves: Array(10).fill(''), totalDifficulty: 0 },
            { name: '', moves: Array(10).fill(''), totalDifficulty: 0 }
          ],
          selectedRoutines: ['', '', ''],
          checkboxes: Array(10).fill(false),
          additionalMarkings: Array(10).fill(false),
          validationStatus: {
            isComplete: false,
            isFirstExerciseValid: false,
            isFullyValid: false,
            isFirstExerciseSelected: false
          }
        });
      }

      setInitialLoadComplete(true);
      setLoading(false);
    } catch (error) {
      handleApiError(error, 'Fehler beim Laden der Wettkampfkarte.');
      setLoading(false);
    }
  };

  const handleScorecardDataChange = (data: ScorecardFormData) => {
    // Im Nur-Lesen-Modus keine Änderungen am State vornehmen
    if (!readOnly) {
      setFormData(data);
    }
  };

  const handleSaveAndClose = async () => {
    if (!formData) return;
    
    try {
      setSaving(true);
      
      await competitionCardsService.saveCompetitionCard(
        competitionId,
        clubId,
        athleteId,
        {
          exercises: formData.exercises,
          validationStatus: formData.validationStatus,
          selectedRoutines: formData.selectedRoutines,
          checkboxes: formData.checkboxes,
          additionalMarkings: formData.additionalMarkings
        },
        { cardIndex, extraCardCompetitionGroup: cardTitle }
      );
      
      setSaving(false);
      onClose(true);
    } catch (error) {
      handleApiError(error, 'Fehler beim Speichern der Wettkampfkarte.');
      setSaving(false);
    }
  };

  const handleCloseDialog = () => {
    onClose(card !== null);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleCloseDialog}
      fullWidth 
      maxWidth="lg"
      aria-labelledby="scorecard-dialog-title"
    >
      <DialogTitle id="scorecard-dialog-title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {cardTitle
              ? `Wettkampfkarte 2 (${cardTitle}): ${athleteName}`
              : `Wettkampfkarte: ${athleteName} (${competitionGroup})`}
          </Typography>
          {formData && (
            <Chip 
              icon={getStatusIcon(formData.validationStatus)} 
              label={getStatusText(formData.validationStatus)} 
              color={getStatusColor(formData.validationStatus) as any}
              size="small" 
            />
          )}
          <IconButton
            aria-label="close"
            onClick={handleCloseDialog}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {readOnly && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Die Abgabefrist für Wettkampfkarten ist abgelaufen. Du kannst die Karte nur noch anschauen.
          </Alert>
        )}        
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : (
          <TrampolineScorecard 
            initialData={formData || undefined}
            onChange={handleScorecardDataChange}
            readOnly={readOnly}
            competitionGroup={competitionGroup}
          />
        )}
      </DialogContent>
      <DialogActions>
      <Button 
          onClick={handleCloseDialog} 
          color="inherit"
        >
          {readOnly ? 'Schließen' : 'Abbrechen'}
        </Button>
        {!readOnly && (
          <Button 
            onClick={handleSaveAndClose} 
            color="primary" 
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            Speichern
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TrampolineScorecardDialog;